import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { AddToCart } from "./AddToCart";
import type { DiscountCandidate } from "./DiscountCandidate";
import type { DiscountOnLines } from "./DiscountOnLines";
import type { LineItemRef } from "./LineItemRef";
import type { LineItemStaff } from "./LineItemStaff";
import type { OrderAmountsBase } from "./OrderAmountsBase";
import type { OrderDTOBase } from "./OrderDTOBase";
import type { OrderLineDetail } from "./OrderLineDetail";
import type { OrderPaymentRef } from "./OrderPaymentRef";
import type { OrderPaymentStaff } from "./OrderPaymentStaff";
import type { OrderRef } from "./OrderRef";
import type { OrderRefundStaff } from "./OrderRefundStaff";
import type { OrderSource } from "./OrderSource";
import type { OrderStatus } from "./OrderStatus";
import type { PaymentAttempt } from "./PaymentAttempt";
import type { PersonRef } from "./PersonRef";
import type { PersonStaff } from "./PersonStaff";
import type { PrinterRef } from "./PrinterRef";
import type { StartPayment } from "./StartPayment";
import type { TrackedModernBase } from "./TrackedModernBase";

export class OrderStaff implements OrderDTOBase, TrackedModernBase, OrderAmountsBase {
  constructor(public readonly assignId: number, public readonly orderStatus: OrderStatus | null, public readonly orderSource: OrderSource | null, public readonly receiptPrint: boolean | null, public readonly receiptEmail: boolean | null, public readonly receiptPrinter: PrinterRef | null, public readonly receiptPrintQueuedOn: Date | null, public readonly receiptEmailSentOn: Date | null, public readonly personId: PersonStaff | null, public readonly orderUserId: number | null, public readonly amountPaidCents: number | null, public readonly settlementFundingForId: number | null, public readonly amountRefundedCents: number | null, public readonly amountCapturableCents: number | null, public readonly squareOrderId: string | null, public readonly version: number | null, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly amountSubtotalCents: number | null, public readonly amountDiscountsCents: number | null, public readonly amountTaxesCents: number | null, public readonly amountTotalCents: number | null, public readonly lineItems: Array<LineItemStaff> | null, public readonly orderRefunds: Array<OrderRefundStaff> | null, public readonly payments: Array<OrderPaymentStaff> | null) {
    this.cbiServiceContext = null;
    this.personIdLoaded = this.personId != null;
    this.personIdCached = this.personId;
    this.lineItemsLoaded = this.lineItems != null;
    this.lineItemsCached = this.lineItems;
    this.orderRefundsLoaded = this.orderRefunds != null;
    this.orderRefundsCached = this.orderRefunds;
    this.paymentsLoaded = this.payments != null;
    this.paymentsCached = this.payments;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private personIdLoaded: boolean;
  private personIdCached: PersonStaff | null;
  private lineItemsLoaded: boolean;
  private lineItemsCached: Array<LineItemStaff> | null;
  private orderRefundsLoaded: boolean;
  private orderRefundsCached: Array<OrderRefundStaff> | null;
  private paymentsLoaded: boolean;
  private paymentsCached: Array<OrderPaymentStaff> | null;
  async personIdLazy(): Promise<PersonStaff | null> {
    if (!this.personIdLoaded) {
      this.personIdCached = await this.cbiServiceContext!.orderService().getOrderStaffPersonId(this.assignId);
      this.personIdLoaded = true;
    }
    return this.personIdCached;
  }
  async lineItemsLazy(): Promise<Array<LineItemStaff>> {
    if (!this.lineItemsLoaded) {
      this.lineItemsCached = await this.cbiServiceContext!.orderService().getOrderStaffLineItems(this.assignId);
      this.lineItemsLoaded = true;
    }
    return this.lineItemsCached!;
  }
  async orderRefundsLazy(): Promise<Array<OrderRefundStaff>> {
    if (!this.orderRefundsLoaded) {
      this.orderRefundsCached = await this.cbiServiceContext!.orderService().getOrderStaffOrderRefunds(this.assignId);
      this.orderRefundsLoaded = true;
    }
    return this.orderRefundsCached!;
  }
  async paymentsLazy(): Promise<Array<OrderPaymentStaff>> {
    if (!this.paymentsLoaded) {
      this.paymentsCached = await this.cbiServiceContext!.orderService().getOrderStaffPayments(this.assignId);
      this.paymentsLoaded = true;
    }
    return this.paymentsCached!;
  }
  async addToCart(request: AddToCart): Promise<LineItemRef> {
    return await this.cbiServiceContext!.orderService().addToCartStaff({ order: { assignId: this.assignId }, request: request });
  }
  async applyDiscountFromCode(code: string): Promise<void> {
    return await this.cbiServiceContext!.orderService().applyDiscountFromCodeStaff({ order: { assignId: this.assignId }, code: code });
  }
  async applyDiscountToLineItems(request: DiscountOnLines): Promise<void> {
    return await this.cbiServiceContext!.orderService().applyDiscountToLineItemsStaff({ order: { assignId: this.assignId }, request: request });
  }
  async cancel(): Promise<void> {
    return await this.cbiServiceContext!.orderService().cancelStaff({ assignId: this.assignId });
  }
  async changeBuyer(person: PersonRef): Promise<OrderRef> {
    return await this.cbiServiceContext!.orderService().changeBuyerStaff({ order: { assignId: this.assignId }, person: person });
  }
  async configureReceiptDelivery(printReceipt: boolean, emailReceipt: boolean, printer: PrinterRef | null): Promise<void> {
    return await this.cbiServiceContext!.orderService().configureReceiptDeliveryStaff({ order: { assignId: this.assignId }, printReceipt: printReceipt, emailReceipt: emailReceipt, printer: printer });
  }
  async eligibleDiscounts(): Promise<Array<DiscountCandidate>> {
    return await this.cbiServiceContext!.orderService().eligibleDiscountsStaff({ assignId: this.assignId });
  }
  async orderLineDetails(): Promise<Array<OrderLineDetail>> {
    return await this.cbiServiceContext!.orderService().orderLineDetailsStaff({ assignId: this.assignId });
  }
  async pay(): Promise<void> {
    return await this.cbiServiceContext!.orderService().payStaff({ assignId: this.assignId });
  }
  async reopen(): Promise<void> {
    return await this.cbiServiceContext!.orderService().reopenStaff({ assignId: this.assignId });
  }
  async retryDeliveries(): Promise<void> {
    return await this.cbiServiceContext!.orderService().retryDeliveriesStaff({ assignId: this.assignId });
  }
  async retryReceiptDelivery(): Promise<void> {
    return await this.cbiServiceContext!.orderService().retryReceiptDeliveryStaff({ assignId: this.assignId });
  }
  async unapplyDiscountFromLineItems(request: DiscountOnLines): Promise<void> {
    return await this.cbiServiceContext!.orderService().unapplyDiscountFromLineItemsStaff({ order: { assignId: this.assignId }, request: request });
  }
  async completePayment(): Promise<void> {
    return await this.cbiServiceContext!.paymentService().completePaymentStaff({ assignId: this.assignId });
  }
  async paymentAttempts(): Promise<Array<PaymentAttempt>> {
    return await this.cbiServiceContext!.paymentService().paymentAttemptsStaff({ assignId: this.assignId });
  }
  async startPayment(request: StartPayment): Promise<OrderPaymentRef> {
    return await this.cbiServiceContext!.paymentService().startPaymentStaff({ order: { assignId: this.assignId }, request: request });
  }
}
